import {
  FluidLinearProgressConfig,
  FluidProgressType,
  FluidTheme,
  FluidThemeInterface,
  FluidWeight,
} from '@lmig/fluid-core';
import { generateUniqueId } from '@lmig/fluid-core/lib/helpers/string.helpers';
import {
  Component,
  ComponentInterface,
  Element,
  h,
  Prop,
  State,
  Watch,
} from '@stencil/core';

import { LINEAR_PROGRESS_INVALID_VALUE } from '../../constants/constants';
import { useI18n } from '../../providers/fluid-i18n-provider/adapter/fluid-i18n.adapter';
import { FluidI18nStore } from '../../providers/fluid-i18n-provider/store/fluid-i18n.store';
import { Utils } from '../../utils/utils';
import {
  LinearProgressIndicator,
  LinearProgressLabel,
} from './components/fluid-linear-progress-components';

/**
 * @displayName Linear Progress
 *
 * @contextData FLUID Linear Progress is an animated visual indicator that a process, task, or progress is currently underway. It can be indeterminate, which means that it does not express a specific wait time or display loading progress, or determinate, which means that it does indicate progress toward completion / how long a process will take.
 *
 * @builder true
 */
@Component({
  tag: 'fluid-linear-progress',
  styleUrl: 'fluid-linear-progress.scss',
  shadow: true,
})
export class FluidLinearProgress
  implements ComponentInterface, FluidThemeInterface
{
  // ================================================================ //
  // -- Own Properties (Private)

  /**
   * Normalized value that indicates "value" Prop's relation to "max"
   * Prop on a scale of 0-100. Used to set progress bar's width.
   */
  private _progressValue = 0;

  // ================================================================ //
  // -- Host Element

  /**
   * Reference to the host element.
   */
  @Element() host: HTMLFluidLinearProgressElement;

  // ================================================================ //
  // -- Props

  /**
   * What theme settings should the component use?
   */
  @Prop({ mutable: true }) theme: FluidTheme = FluidTheme.CORP;

  /**
   * If FluidThemeProvider is used, should this component override the
   * global theme with its local theme?
   */
  @Prop({ mutable: true }) overrideGlobalTheme: boolean;

  /**
   * Determines whether Linear Progress is a determinate or indeterminate
   * indicator. Linear Progress is indeterminate by default, which means
   * that it will NOT indicate wait time / time to completion (e.g.
   * attempting to reconnect to a server). When this Prop is true, Linear
   * Progress will be a determinate indicator, meaning that it WILL show
   * progress toward completion / how long a process will take to finish
   * (e.g., downloading a file of a known size).
   */
  @Prop({ mutable: true }) determinate = false;

  /**
   * For determinate Linear Progress, sets the data format that the
   * current value should display.
   */
  @Prop({ mutable: true }) type: FluidProgressType = FluidProgressType.PERCENT;

  /**
   * For determinate Linear Progress, sets the current progress/completion
   * status toward the "max" Prop value.
   */
  @Prop({ mutable: true }) value = 0;

  /**
   * For determinate Linear Progress, sets the maximum value on the progress
   * bar. When the "value" Prop equals the "max" Prop, the progress bar will
   * be shown as full/complete.
   */
  @Prop({ mutable: true }) max = 100;

  /**
   * Optional descriptive title/label to be displayed above the progress bar.
   */
  @Prop({ mutable: true }) label: string;

  /**
   * If "label" Prop is provided, overrides the label element's default "id"
   * attribute value.
   */
  @Prop({ mutable: true }) labelId: string = generateUniqueId();

  /**
   * Sets the Linear Progress component's "line weight" (i.e. the height
   * & thickness of the progress bar).
   */
  @Prop({ mutable: true }) weight: FluidWeight = FluidWeight.MEDIUM;

  /**
   * Allows all Props to be set via a single configuration object.
   */
  @Prop({ mutable: true }) config: FluidLinearProgressConfig;

  @Watch('config')
  onConfigChange() {
    Utils.selectiveMapConfigToLocal(this, this.config);
  }

  // ================================================================ //
  // -- Lifecycle Hooks

  /**
   * Called every time the component is connected to the DOM.
   * @action: setGlobalTheme - updates theme to match global theme & subscribes to changes
   * @action: validateValues - validates "value" & "max" Prop values
   * @action: calculateProgressValue - calculates normalized value
   */
  connectedCallback(): void {
    useI18n.bind(this)();
    Utils.selectiveMapConfigToLocal(this, this.config);
    Utils.setGlobalTheme(this);
    this.validateValues();
    this.calculateProgressValue();
  }

  /**
   * Called every time the component is disconnected from the DOM.
   * @action deregisterElement - removes the element from the i18n Store registry
   */
  disconnectedCallback() {
    this._i18nStore?.deregisterElement(this._translateId);
  }

  /**
   * Called when component will be updated due to @Prop or @State change.
   * @action: validateValues - validates "value" & "max" Prop values
   * @action: calculateProgressValue - calculates normalized value
   */
  componentWillUpdate() {
    this.validateValues();
    this.calculateProgressValue();
  }

  // ================================================================ //
  // -- Local Methods

  /**
   * Ensures "max" & "value" Props are numbers - if not, outputs console
   * warning & resets invalid Prop value(s) to default. Also verifies that
   * "value" Prop is >= 0 AND <= "max" Prop value.
   */
  validateValues(): void {
    if (!Utils.isNumber(this.max)) {
      Utils.consoleWarn(this.host, LINEAR_PROGRESS_INVALID_VALUE('max', 100));
      this.max = 100;
    }

    if (!Utils.isNumber(this.value)) {
      Utils.consoleWarn(this.host, LINEAR_PROGRESS_INVALID_VALUE('value', 0));
      this.value = 0;
    } else {
      this.value = Math.min(this.max, Math.max(0, this.value));
    }
  }

  /**
   * If Linear Progress is determinate, calculates normalized "progress
   * value" that indicates "value" Prop's relation to "max" Prop on a
   * scale of 0-100. If Linear Progress is indeterminate, resets "progress
   * value" to 0.
   */
  calculateProgressValue(): void {
    if (this.determinate) {
      this._progressValue = Math.round((this.value / this.max) * 100);
    } else {
      this._progressValue = 0;
    }
  }

  // ================================================================ //
  // -- Render (Main)

  render() {
    return (
      <div class={`fluid-linear-progress theme-${this.theme}`}>
        <LinearProgressLabel
          label={
            this._i18nStore?.translate(this.fluidTranslate?.label) || this.label
          }
          labelId={this.labelId}
        />
        <LinearProgressIndicator
          determinate={this.determinate}
          type={this.type}
          value={this.value}
          max={this.max}
          progressValue={this._progressValue}
          weight={this.weight}
          label={
            this._i18nStore?.translate(this.fluidTranslate?.label) || this.label
          }
          labelId={this.labelId}
        />
      </div>
    );
  }

  // ================================================================ //
  // -- Internationalisation

  /* @internal */
  @Prop({ mutable: true }) fluidTranslate: { label?: string };
  private _translateId: string = generateUniqueId();
  @State() private _i18nStore: FluidI18nStore | undefined;
}
